import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map, shareReplay, take} from 'rxjs/operators';

import {HttpService} from 'src/app/services/http.service';
import {CountryModel} from '../shared/models/CountryModel';
import {CurrencyModel} from '../shared/models/CurrencyModel';
import {BankHolidayResponse} from '../shared/models/BankHolidayResponse.model';
import {CountriesService} from './countries.service';
import {UsStatesService} from './us-states.service';

@Injectable({
  providedIn: 'root'
})
export class ResourceService {
  private readonly COUNTRIES_BASE_ENDPOINT = 'countries';

  private _allCountries: CountryModel[];
  private _allStates: Array<{ abbreviation: string; name: string }>;
  private _allEntityTypes: Array<{ value: string; name: string }>;
  private _allOfficerTitles: Array<{ value: string; name: string }>;

  public get allCountries() {
    return this._allCountries;
  }

  public get allStates() {
    return this._allStates;
  }

  public get allEntityTypes() {
    return this._allEntityTypes;
  }

  public get allOfficerTitles() {
    return this._allOfficerTitles;
  }

  allCountries$ = this.http.getTyped<CountryModel[]>(this.COUNTRIES_BASE_ENDPOINT).pipe(
    map(countries => countries.map(c => CountryModel.fromJson(c))),
    shareReplay(1));

  constructor(private http: HttpService, private countriesService: CountriesService, private usStatesService: UsStatesService) {
    this.populateCountries();
    this.populateStates();
    this.populateEntityTypes();
    this.populateOfficerTitles();
  }

  public getCountryById$(countryId: number) {
    return this.allCountries$.pipe(take(1), map(allCountries => allCountries.find(c => c.id === countryId)));
  }

  public getSendingCountries(sendingCurrencyIso: string): Observable<CountryModel[]> {
    if (sendingCurrencyIso) {
      return this.http.get(`countries/sendingCountries/${sendingCurrencyIso}`).pipe(map((response: any) => response.map(item => CountryModel.fromJson(item))));
    }
    return Observable.create(null);
  }

  public getReceiveCountries(receiveCurrencyIso: string): Observable<CountryModel[]> {
    if (receiveCurrencyIso) {
      return this.http.get(`countries/receiveCountries/${receiveCurrencyIso}`).pipe(map((response: any) => response.map(item => CountryModel.fromJson(item))));
    }
    return Observable.create(null);
  }

  private populateCountries() {
    this._allCountries = this.countriesService.allCountries;
  }

  private populateStates() {
    this._allStates = this.usStatesService.usStates;
  }

  private populateEntityTypes() {
    this._allEntityTypes = [
      {value: 'Corporation', name: 'Corporation'},
      {value: 'LLC', name: 'LLC'},
      {value: 'Partnership', name: 'Partnership'}
    ];
  }

  private populateOfficerTitles() {
    this._allOfficerTitles = [
      {value: 'CEO', name: 'CEO'},
      {value: 'COO', name: 'COO'},
      {value: 'CFO', name: 'CFO'},
      {value: 'President', name: 'President'},
      {value: 'BenefitsAdministrationOfficer', name: 'Benefits Administration Officer'},
      {value: 'CIO', name: 'CIO'},
      {value: 'VP', name: 'VP'},
      {value: 'AVP', name: 'AVP'},
      {value: 'Treasurer', name: 'Treasurer'},
      {value: 'Secretary', name: 'Secretary'},
      {value: 'Controller', name: 'Controller'},
      {value: 'Manager', name: 'Manager'},
      {value: 'Partner', name: 'Partner'},
      {value: 'Member', name: 'Member'}
    ];
  }

  getCountryByCode(countryCode: string): CountryModel {
    if (!countryCode || !this.allCountries || this.allCountries.length === 0) {
      return new CountryModel();
    }
    return this.allCountries.find(c => c.code === countryCode);
  }

  getCountryById(countryId: number): CountryModel {
    if (!countryId || !this.allCountries || this.allCountries.length === 0) {
      return new CountryModel();
    }
    return this.allCountries.find(c => c.id === countryId);
  }

  getCountryByName(countryName: string): CountryModel {
    if (!countryName || !this.allCountries || this.allCountries.length === 0) {
      return new CountryModel();
    }

    return this.allCountries.find(c => c.name === countryName);
  }

  public getOutboundCurrencies(): Observable<CurrencyModel[]> {
    return this.http.getTyped<any>('currencies/getCurrencyConverterCurrencies').pipe(
      map(json => {
        const model: CurrencyModel[] = new Array<CurrencyModel>();
        json.forEach(element => {
          model.push(CurrencyModel.fromJson(element));
        });
        return model;
      })
    );
  }

  public generateHolidayCountriesString(bankHolidays: BankHolidayResponse[]): string {

    try {
      if (!bankHolidays || bankHolidays.length === 0) {
        return '';
      }
      // reduce duplicate countries so only 1 country shows even if there are multiple holidays for a specific country
      const distinctCountryIds = Array.from(new Set(bankHolidays.map((item: BankHolidayResponse) => item.countryId)));
      const separator = distinctCountryIds.length === 2 ? ' and in ' : ', ';
      const holidayString = distinctCountryIds.map(countryId => this.getCountryById(countryId).name).join(separator);
      return holidayString;
    } catch (error) {
      return '';
    }
  }

  /**
   * Get a list of forbidden characters in holding name
   */
  get forbiddenCharactersInHoldingName(): string[] {
    return [
      '/',
      '\\'
    ];
  }
}
